import {
    Address,
    Addresses,
    CreateAddress,
    EditAddress,
} from "@beneficiary/domain/models/address.model";
import { Order } from "@core/domain/models/order.model";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { inject, injectable } from "inversify";
import { AddressDatasource } from "../datasource/address.datasource";
import { AddressSearchFilters } from "../dto/address.dto";
import { BeneficiaryAddressError } from "@beneficiary/domain/errors/beneficiary.error";
import { AddressMapperError } from "@beneficiary/domain/errors/address/address-mapper.error";

@injectable()
export class AddressRepository {
    constructor(
        @inject(AddressDatasource)
        private readonly addressDatasource: AddressDatasource,
    ) { }

    async create(
        createAddress: CreateAddress,
    ): Promise<Either<BeneficiaryAddressError | AddressMapperError, Address>> {
        return this.addressDatasource.create(createAddress);
    }

    async edit(address: EditAddress): Promise<Either<BeneficiaryAddressError | AddressMapperError, Address>> {
        return this.addressDatasource.update(address);
    }

    async getAllBy(
        pagination: Pagination,
        filters?: AddressSearchFilters,
        order?: Order<Address>,
    ): Promise<Either<BeneficiaryAddressError, Addresses>> {
        const addressesSummary = await this.addressDatasource.fetchBy(
            pagination,
            filters,
            order,
        );

        return addressesSummary.mapLeft(() => new BeneficiaryAddressError("fetchAllAddressesByError"));
    }

    async findById(addressId: number): Promise<Either<BeneficiaryAddressError | AddressMapperError, Address>> {
        const addressResult = await this.addressDatasource.fetchById(addressId);

        if (addressResult.isLeft())
            return Either.Left(addressResult.getLeftOrThrow());

        const { address } = addressResult.getOrThrow();

        return Either.Right(address);
    }

    async delete(addressId: number): Promise<Either<BeneficiaryAddressError, boolean>> {
        return this.addressDatasource.delete(addressId);
    }
}
